import _ from 'underscore';

import {
  TaskData,
  WorksheetTaskLoadingStateMapping,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';

const WORKSHEET_TASK_STATUSES_TO_SHOW_ACTION_DROPDOWN = [
  WorksheetTaskStatusType.PENDING,
  WorksheetTaskStatusType.IN_PROGRESS,
];

export const BUTTON_DISABLED_PREV_RUN_NOT_COMPLETE =
  'Button is disabled because the previous unit operation has not been completed.';

export const BUTTON_DISABLED_ALL_TASKS_NOT_CREATED =
  'Button is disabled because this worksheet is still being compiled.';

export interface WorksheetControlVariables {
  isWorksheetPending: boolean;
  worksheetLastActionText: string | null;
  isWorksheetStatusBadgeDisabled: boolean;
  showWorksheetStatusDropdownButton: boolean;
  worksheetStatusBadgeTooltipText: string | null;
}

interface WorksheetControlOptions {
  /** Whether the worksheet still has step documents that have not been saved */
  hasUnsavedStepDocuments: boolean;
  /** Whether all PMEIs have their method tasks linked */
  areAllMethodTasksCreated: boolean;
  /**
   * Whether all previous PMEIs' tasks have reached terminal status
   * (used in delayed mapping / split pool where current PMEIs may not have tasks yet)
   */
  previousPMEITasksCompleted?: boolean;
  /** Whether delayed mapping (split pool) is enabled */
  isDelayedMappingEnabled?: boolean;
}

/**
 * Function to output booleans that control the UI behavior of WorksheetPageHeader & other worksheet-level components
 * @param worksheetTaskData task data of worksheet output by function WorksheetUtils.extractTaskData
 * @param options see interface WorksheetControlOptions
 * @returns see interface WorksheetControlVariables
 */
export const getWorksheetControlVariables = (
  worksheetTaskData: TaskData,
  options: WorksheetControlOptions
): WorksheetControlVariables => {
  const {
    hasUnsavedStepDocuments,
    areAllMethodTasksCreated,
    previousPMEITasksCompleted = true,
    isDelayedMappingEnabled = false,
  } = options;
  const hasTaskData = !_.isEmpty(worksheetTaskData);
  // In the standard (non-delayed) case, prevMethodComplete is true when task data exists
  // (i.e. tasks have been created for this worksheet's PMEIs).
  // In delayed mapping, PMEIs have status PENDING but no task IDs — use previous PMEI
  // task completion to determine if this worksheet is ready to start.
  const hasNoLinkedTasks = isDelayedMappingEnabled && hasTaskData && !worksheetTaskData.taskIds?.length;
  const prevMethodComplete = hasNoLinkedTasks ? previousPMEITasksCompleted : hasTaskData;
  const tasksNotReady = hasNoLinkedTasks ? !previousPMEITasksCompleted : !areAllMethodTasksCreated;
  const noStatusAndNotReady = !worksheetTaskData.status && tasksNotReady;

  const isWorksheetPending =
    !worksheetTaskData.status ||
    worksheetTaskData.status === WorksheetTaskStatusType.PENDING ||
    tasksNotReady;

  // assemble text on page header describing worksheet's last action
  const worksheetLastActionVerb =
    worksheetTaskData.status === WorksheetTaskStatusType.PENDING
      ? null
      : worksheetTaskData.status === WorksheetTaskStatusType.IN_PROGRESS
        ? 'started'
        : worksheetTaskData.status?.toLowerCase();
  const worksheetLastActionText = worksheetLastActionVerb
    ? worksheetTaskData.status &&
      Object.values(WorksheetTaskLoadingStateMapping).includes(worksheetTaskData.status)
      ? `Run ${worksheetLastActionVerb}...`
      : `Run ${worksheetLastActionVerb} on ${worksheetTaskData.timestamp}`
    : null;

  // determine if WorksheetStatusBadge should be disabled
  const isWorksheetStatusBadgeDisabled =
    hasUnsavedStepDocuments ||
    noStatusAndNotReady ||
    !prevMethodComplete ||
    tasksNotReady ||
    Boolean(
      worksheetTaskData.status &&
      Object.values(WorksheetTaskLoadingStateMapping).includes(worksheetTaskData.status)
    );

  // determine if the dropdown button for WorksheetStatusBadge should be shown
  const showWorksheetStatusDropdownButton = Boolean(
    !worksheetTaskData.status ||
    !prevMethodComplete ||
    WORKSHEET_TASK_STATUSES_TO_SHOW_ACTION_DROPDOWN.includes(worksheetTaskData.status)
  );
  // when worksheetStatusBadgeTooltipText is null we disable tooltip from showing
  const worksheetStatusBadgeTooltipText = !prevMethodComplete
    ? BUTTON_DISABLED_PREV_RUN_NOT_COMPLETE
    : tasksNotReady
      ? BUTTON_DISABLED_ALL_TASKS_NOT_CREATED
      : null;

  return {
    isWorksheetPending,
    worksheetLastActionText,
    isWorksheetStatusBadgeDisabled,
    showWorksheetStatusDropdownButton,
    worksheetStatusBadgeTooltipText,
  };
};
